import styled from "styled-components";
import { LabelText, MediumText } from "../typography";
import { COLORS } from "../../assets/utils/enums/colors";
import eyeOpenIcon from "../../assets/images/icons/eye-open-icon.svg";
import eyeCloseIcon from "../../assets/images/icons/eye-close-icon.svg";
import sendInviteIcon from '../../assets/images/icons/send-icon.svg';
import uaFlag from "../../assets/images/ua-flag.png";
import React, { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import ErrorBlock from "./error-text";
import Dropdown from "./dropdown";
import helpIcon from '../../assets/images/icons/circle-info-icon.svg';
import { DropdownTypeEnum } from "../../assets/utils/enums/table";
import { NameIdType } from "../../assets/utils/enums/project";

interface InputProps {
  attribute: string;
  label: string;
  errorText: string;
  placeholder: string;
  value: string;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  item?: NameIdType;
  setItem?: React.Dispatch<React.SetStateAction<NameIdType>>;
  currency?: string;
  isTooltip?: boolean;
  setCurrency?: React.Dispatch<React.SetStateAction<string>>;
  contactsArr?:
  | { id: number; fullName: string; email: string }[]
  | { fullName: string; email: string }[];
  companyArr?: { id: number; name: string; responsibleEmail: string }[];
  itemsNameIdArr?: NameIdType[];
}

export function isValidUrl(url: string): boolean {
  const urlRegex =
    /^(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/[a-zA-Z0-9]+\.[^\s]{2,}|[a-zA-Z0-9]+\.[^\s]{2,})$/i;
  return urlRegex.test(url);
}

const Input: React.FC<InputProps> = ({
  attribute,
  label,
  errorText,
  placeholder,
  value,
  setValue,
  currency,
  setCurrency,
  contactsArr,
  companyArr,
  item,
  setItem,
  itemsNameIdArr,
  isTooltip
}) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const formatWebsite = (value: string): string => {
    let formatted = value.trim();

    if (!formatted) return formatted;

    if (!/^https?:\/\//i.test(formatted)) {
      formatted = `https://${formatted}`;
    }

    formatted = formatted.replace(/\s+/g, "").replace(/\/+$/, "");

    return formatted;
  };

  return (
    <InputStyle className="d-flex flex-column ">
      <div className={`w-100 d-flex align-items-center justify-content-between ${!label ? "pt-1" : ""}`}>
        {label && <LabelText>{label}</LabelText>}
        {isTooltip && <img
          src={helpIcon}
          className="help-icon mb-2"
          alt="help-icon"
          data-tooltip-id="info-tooltip" data-tooltip-content="Після вибору проєкту, Ви зможете обрати лише ті продажі які з ним пов’язані та навпаки."
        />}
      </div>
      {attribute === "phone" ? (
        <PhoneInput
          country={"ua"}
          value={value}
          onChange={(val) => {
            if (!val.startsWith("+380")) {
              setValue("+380" + val.replace(/[^0-9]/g, "").slice(3));
            } else {
              setValue(val);
            }
          }}
          onlyCountries={["ua"]}
          masks={{ ua: ".. .. .. ..." }}
          inputProps={{
            required: true,
            onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
              if (!e.target.value.startsWith("+380")) {
                setValue("+380");
              }
            },
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              const input = e.target as HTMLInputElement;
              if (
                input.selectionStart! <= 4 &&
                ["Backspace", "Delete"].includes(e.key)
              ) {
                e.preventDefault();
              }
            },
          }}
        />
      ) : (
        <span className="d-flex align-items-center justify-content-between">
          {attribute === "send-email-invite" && <img src={sendInviteIcon} alt="send-invite-icon" />}
          {attribute === "budget" && (
            <Dropdown
              checkedValue={currency!}
              setChecked={setCurrency!}
              type={DropdownTypeEnum.CURRENCY}
            />
          )}
          {attribute === DropdownTypeEnum.CLIENT ||
            attribute === DropdownTypeEnum.CONTACT ||
            attribute === DropdownTypeEnum.MEMBERS ? (
            <Dropdown
              type={attribute}
              checkedValue={value}
              setChecked={setValue}
              contactsArr={contactsArr}
            />
          ) : attribute === DropdownTypeEnum.COMPANY ? (
            <Dropdown
              type={attribute}
              checkedValue={value}
              setChecked={setValue}
              companyArr={companyArr}
            />
          ) : attribute === DropdownTypeEnum.PROJECTS ||
            attribute === DropdownTypeEnum.SELLINGS ? (
            <Dropdown
              type={attribute}
              checkedValue={value}
              setChecked={setValue}
              item={item}
              setItem={setItem}
              itemsNameIdArr={itemsNameIdArr}
              placeholder={placeholder}
            />
          ) : (
            <input
              id={attribute}
              name={attribute}
              placeholder={placeholder}
              type={
                attribute !== "email" && !attribute.includes("password")
                  ? "text"
                  : attribute === "email"
                    ? "email"
                    : showPassword
                      ? "text"
                      : "password"
              }
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
              }}
              onBlur={() => {
                if (attribute === "website") {
                  const formatted = formatWebsite(value);
                  setValue(formatted);
                }
              }}
              style={{
                color:
                  attribute === "website" && isValidUrl(value)
                    ? COLORS.blue
                    : COLORS.secondary1,
                textDecoration:
                  attribute === "website" && isValidUrl(value)
                    ? "underline"
                    : "none",
                transition: "0.3s ease-in-out",
              }}
              onKeyDown={(e) => {
                if (attribute === "budget") {
                  if (!/[0-9]/.test(e.key) && e.key !== "Backspace") {
                    e.preventDefault();
                  }
                }
              }}
              autoComplete={
                attribute.includes("password") ? "current-password" : attribute
              }
            />
          )}

          {attribute.includes("password") && (
            <img
              src={showPassword ? eyeCloseIcon : eyeOpenIcon}
              alt="eye-icon"
              onClick={() => setShowPassword((prev) => !prev)}
            />
          )}
        </span>
      )}
      {errorText && <ErrorBlock errorText={errorText} />}
    </InputStyle>
  );
};

export default Input;

export const InputStyle = styled.span`
  width: 100%;
  padding: 10px 0;
  .react-switch {
    margin-left: 20px !important;
    height: 100% !important;
  }

  span,
  .react-tel-input {
    width: 100%;
    border: 1px solid transparent;
    border-radius: 24px;
    background-color: ${COLORS.gray};
    padding: 10px 20px;
    input {
      outline: none;
      margin: 0;
      padding: 0;
      color: ${COLORS.secondary1};
      caret-color: ${COLORS.secondary1};
      ${MediumText.componentStyle.rules.join("")}
      border: none;
      width: 85%;
      flex-grow: 1;
      background-color: transparent;

      &::placeholder {
        color: ${COLORS["bg-text-color"]};
      }
      &:is(
          :-webkit-autofill,
          :autofill,
          :-webkit-autofill:hover,
          :-webkit-autofill:focus,
          :-internal-autofill-selected
        ) {
        -webkit-text-fill-color: ${COLORS.secondary1};
        transition: background-color 5000s ease-in-out 0s !important;
        background-color: transparent !important;
      }
    }
    &:focus {
      box-shadow: 0px 0px 2.5px -1px ${COLORS.secondary1};
    }

    img {
      height: 20px;
      width: 20px;
      cursor: pointer;
    }
  }
  [data-type="dropdown-label"] {
    padding: 0 6px 0 2px !important;
  }
  ${LabelText} {
    padding-bottom: 8px;
    padding-left: 20px;
    color: ${COLORS.secondary1};
  }

  .react-tel-input {
    display: flex;
    flex-direction: row-reverse;
    gap: 20px;
    align-items: center;
    justify-content: space-between;
    outline: none;

    .flag-dropdown {
      background-color: transparent;
      /* position: absolute; */
      /* padding: 20px; */
      border-radius: 24px;
      border: none;
      left: 0px;
      position: relative;
    }
    .selected-flag {
      display: flex;
      justify-content: center;
      align-items: center;
      width: auto;
      padding: 0;
      border-radius: 0;
      padding-left: 0px;
    }
    .arrow {
      display: none;
    }
    .form-control {
      width: 100%;
      height: auto;
      border-radius: 24px;
      box-shadow: none;
      position: relative;
    }
    .special-label {
      display: none;
    }
    .flag {
      background-image: url(${uaFlag});
      background-position: center;
      width: 26px;
      height: 18px;
      position: relative;
      top: 0;
      margin: 0;
    }
    .country-name,
    .dial-code {
      ${MediumText.componentStyle.rules.join("")}
    }
    .country-name {
      color: ${COLORS.secondary1};
    }
    .dial-code {
      color: ${COLORS.gray};
    }
    .country-list {
      display: none;
    }
  }

  
`;
